import React, { useState, useRef } from 'react';
import SiteLayout, { PALETTE } from './SiteLayout';
import SEO from '../../components/SEO';
import { BACKEND_URL } from '../../lib/api';

const IMG_HERD = 'https://images.unsplash.com/photo-1500595046743-cd271d694d30?auto=format&fit=crop&w=2000&q=80';

export default function SiteContact() {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', sector: '', currentSystem: '', currentSystemOther: '', message: '', referenceCall: false, website: '' });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');
  // Timestamp when the form first renders, used by the server to reject
  // sub-second bot submissions.
  const formShownAt = useRef(Date.now());

  const systemLabel = form.currentSystem === 'Other'
    ? (form.currentSystemOther ? `Other, ${form.currentSystemOther}` : 'Other')
    : form.currentSystem;

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    const payload = {
      name: form.name,
      company: form.company,
      email: form.email,
      phone: form.phone,
      sector: form.sector,
      current_system: form.currentSystem,
      current_system_other: form.currentSystemOther,
      // Keep legacy `volume` populated so older consumers keep working
      volume: systemLabel,
      message: form.message,
      referenceCall: form.referenceCall,
      source_path: typeof window !== 'undefined' ? window.location.pathname : '',
      referer: typeof document !== 'undefined' ? (document.referrer || '') : '',
      website: form.website,               // honeypot, real users leave this blank
      form_shown_at: formShownAt.current,
    };
    // POST to backend. If the backend rejects (429/400 from spam/rate limit),
    // surface the message so a real user can fall back to email.
    try {
      const res = await fetch(`${BACKEND_URL}/api/marketing/reference-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.json()).detail || ''; } catch { /* noop */ }
        if (res.status === 429) {
          setErr('Too many submissions from this connection. Please try again in a minute or email hello@cloudvoro.com.');
          return;
        }
        if (detail) { setErr(detail); return; }
      }
    } catch (_) { /* non-blocking, fall through to mailto */ }
    const subject = encodeURIComponent(`Keystone enquiry, ${form.company || form.name}${form.referenceCall ? ' [Reference call requested]' : ''}`);
    const body = encodeURIComponent(
      `Name: ${form.name}\nCompany: ${form.company}\nEmail: ${form.email}\nPhone: ${form.phone}\nWhat we make: ${form.sector || 'Not specified'}\nCurrent system: ${systemLabel}\n\nReference call requested: ${form.referenceCall ? 'YES, please introduce me to a current Keystone customer' : 'No'}\n\n${form.message}`
    );
    window.location.href = `mailto:hello@cloudvoro.com?subject=${subject}&body=${body}`;
    setSent(true);
  };

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const inputStyle = {
    width: '100%',
    padding: '14px 16px',
    border: `1px solid ${PALETTE.border}`,
    background: PALETTE.paper,
    fontSize: 15,
    color: PALETTE.ink,
    fontFamily: 'inherit',
    borderRadius: 4,
    outline: 'none',
    transition: 'border-color 0.15s',
  };
  const labelStyle = {
    display: 'block',
    fontSize: 11,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: PALETTE.muted,
    marginBottom: 8,
  };

  return (
    <SiteLayout>
      <SEO
        title="Contact Keystone, A direct line to the team"
        description="Tell us your current system and what you make, we book a 20-minute call within one working day. Reference call with an active customer available."
        path="/site/contact"
        keywords="contact Keystone, food ERP demo, Cashel Blue reference call, European food software contact, production software demo, Herbst replacement, Sage food manufacturer"
      />
      {/* HERO */}
      <section data-testid="site-contact-hero" style={{ background: PALETTE.cream, padding: '160px 32px 60px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 20 }}>Talk to us</div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(44px, 6vw, 78px)',
            fontWeight: 700, lineHeight: 0.98, letterSpacing: '-0.03em', color: PALETTE.navy, margin: 0,
          }}>
            Twenty minutes.<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>That's all.</em>
          </h1>
          <p style={{ fontSize: 22, lineHeight: 1.5, color: PALETTE.slate, marginTop: 28, maxWidth: 720 }}>
            We don't run discovery calls. We ask what's broken. If we can fix it, we tell you how. If not, we point you somewhere better.
          </p>
        </div>
      </section>

      {/* FORM + SIDE */}
      <section style={{ background: PALETTE.paper, padding: '60px 32px 120px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 64 }} className="contact-grid">
          {/* Form */}
          <form onSubmit={submit} data-testid="contact-form" style={{
            background: PALETTE.cream,
            border: `1px solid ${PALETTE.border}`,
            padding: 40,
          }}>
            {sent ? (
              <div data-testid="contact-success" style={{ padding: 32, textAlign: 'center' }}>
                <div style={{ fontSize: 48, marginBottom: 16, color: PALETTE.magenta }}>✓</div>
                <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, color: PALETTE.navy, margin: 0 }}>
                  Your email client just opened.
                </h3>
                <p style={{ fontSize: 15, color: PALETTE.slate, marginTop: 12, lineHeight: 1.6 }}>
                  Send the message we drafted, or copy the details to <a href="mailto:hello@cloudvoro.com" style={{ color: PALETTE.magenta }}>hello@cloudvoro.com</a> directly. We reply within one working day.
                </p>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>Book a call</div>
                <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 28, fontWeight: 700, color: PALETTE.navy, margin: 0, letterSpacing: '-0.02em' }}>
                  What's actually broken?
                </h2>
                {/* Honeypot, visually hidden but present in DOM. Real users
                    will never see or fill this; bots that auto-fill every
                    input trip it and are silently rejected server-side. */}
                <input
                  type="text"
                  name="website"
                  autoComplete="off"
                  tabIndex={-1}
                  value={form.website}
                  onChange={update('website')}
                  aria-hidden="true"
                  style={{
                    position: 'absolute', left: '-9999px', top: 'auto',
                    width: 1, height: 1, overflow: 'hidden', opacity: 0,
                  }}
                />
                {err && (
                  <div data-testid="contact-error" style={{
                    marginTop: 20, padding: '10px 14px',
                    background: '#FEF2F2', color: '#B91C1C',
                    border: '1px solid #FCA5A5', borderRadius: 4,
                    fontSize: 13.5, lineHeight: 1.5,
                  }}>{err}</div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 32 }} className="form-row">
                  <div>
                    <label style={labelStyle}>Your name</label>
                    <input data-testid="contact-name" required value={form.name} onChange={update('name')} style={inputStyle} placeholder="Áine Murphy" />
                  </div>
                  <div>
                    <label style={labelStyle}>Company</label>
                    <input data-testid="contact-company" required value={form.company} onChange={update('company')} style={inputStyle} placeholder="Murphy Dairy" />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 20 }} className="form-row">
                  <div>
                    <label style={labelStyle}>Email</label>
                    <input data-testid="contact-email" required type="email" value={form.email} onChange={update('email')} style={inputStyle} placeholder="aine@murphydairy.ie" />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone (optional)</label>
                    <input data-testid="contact-phone" value={form.phone} onChange={update('phone')} style={inputStyle} placeholder="+353 ..." />
                  </div>
                </div>
                <div style={{ marginTop: 20 }}>
                  <label style={labelStyle}>What do you make?</label>
                  <select
                    data-testid="contact-sector"
                    value={form.sector}
                    onChange={update('sector')}
                    style={{ ...inputStyle, appearance: 'none' }}
                  >
                    <option value="">Select what you produce…</option>
                    <option>Dairy & cheese</option>
                    <option>Bakery</option>
                    <option>Meat processing</option>
                    <option>Seafood & fish</option>
                    <option>Prepared food & ready meals</option>
                    <option>Sauces, preserves & ambient</option>
                    <option>Beverages</option>
                    <option>Wholesale & distribution</option>
                    <option>Other / mixed</option>
                  </select>
                </div>
                <div style={{ marginTop: 20 }}>
                  <label style={labelStyle}>Current system being used</label>
                  <select
                    data-testid="contact-current-system"
                    value={form.currentSystem}
                    onChange={update('currentSystem')}
                    style={{ ...inputStyle, appearance: 'none' }}
                  >
                    <option value="">Select your current system…</option>
                    <option>Herbst (Insight / FoodOffice)</option>
                    <option>Sage (50 / 200 / X3)</option>
                    <option>Intact iQ</option>
                    <option>Microsoft Dynamics 365 Business Central / NAV</option>
                    <option>SAP Business One</option>
                    <option>Oracle NetSuite</option>
                    <option>Odoo</option>
                    <option>Epicor</option>
                    <option>Infor M3 / LN</option>
                    <option>SYSPRO</option>
                    <option>Pegasus Opera</option>
                    <option>Exchequer / Advanced</option>
                    <option>QuickBooks / Xero (accounts only)</option>
                    <option>Spreadsheets / paper</option>
                    <option>In-house / custom-built</option>
                    <option>Nothing yet, starting from scratch</option>
                    <option>Other</option>
                  </select>
                </div>
                {form.currentSystem === 'Other' && (
                  <div style={{ marginTop: 12 }} data-testid="contact-current-system-other-wrap">
                    <label style={labelStyle}>Please specify</label>
                    <input
                      data-testid="contact-current-system-other"
                      type="text"
                      value={form.currentSystemOther}
                      onChange={update('currentSystemOther')}
                      style={inputStyle}
                      placeholder="Name of the system you're using"
                      maxLength={120}
                    />
                  </div>
                )}
                <div style={{ marginTop: 20 }}>
                  <label style={labelStyle}>What's the most painful part right now?</label>
                  <textarea
                    data-testid="contact-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={update('message')}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                    placeholder="e.g. Trace queries take 3 hours. Dispatch notes get rejected by Tesco. We can't reconcile QC samples to batches..."
                  />
                </div>
                <div style={{
                  marginTop: 24,
                  padding: '18px 20px',
                  background: '#fff',
                  border: `1px solid ${PALETTE.border}`,
                  borderLeft: `3px solid ${PALETTE.magenta}`,
                  borderRadius: 4,
                }}>
                  <label
                    htmlFor="reference-call-checkbox"
                    style={{
                      display: 'flex', gap: 12, alignItems: 'flex-start', cursor: 'pointer',
                    }}
                  >
                    <input
                      id="reference-call-checkbox"
                      data-testid="contact-reference-call-checkbox"
                      type="checkbox"
                      checked={form.referenceCall}
                      onChange={(e) => setForm(f => ({ ...f, referenceCall: e.target.checked }))}
                      style={{ width: 18, height: 18, marginTop: 2, accentColor: PALETTE.magenta, cursor: 'pointer', flexShrink: 0 }}
                    />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: PALETTE.navy, lineHeight: 1.4 }}>
                        I'd like to speak directly with a current Keystone customer.
                      </div>
                      <div style={{ fontSize: 12.5, color: PALETTE.slate, lineHeight: 1.55, marginTop: 4 }}>
                        Once we've had a brief discovery call we'll broker a direct introduction to one of our active customers. No sales pitch, they'll tell you, in their own words, what works and what doesn't.
                      </div>
                    </div>
                  </label>
                </div>
                <button data-testid="contact-submit" type="submit" style={{
                  marginTop: 24,
                  width: '100%',
                  padding: '18px',
                  background: PALETTE.navy,
                  color: '#fff',
                  border: 'none',
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}>
                  Send the email →
                </button>
                <div style={{ fontSize: 12, color: PALETTE.muted, marginTop: 14, textAlign: 'center', lineHeight: 1.5 }}>
                  Opens your email client with a pre-filled message. Or write us directly: <a href="mailto:hello@cloudvoro.com" style={{ color: PALETTE.magenta }}>hello@cloudvoro.com</a>
                </div>
              </>
            )}
          </form>

          {/* Side info */}
          <aside>
            <div style={{
              background: PALETTE.navy,
              color: '#fff',
              padding: 36,
              marginBottom: 24,
            }}>
              <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 12 }}>Direct line</div>
              <h3 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 24, fontWeight: 700, margin: 0, letterSpacing: '-0.01em' }}>
                Skip the form.
              </h3>
              <p style={{ fontSize: 14, color: '#CBD5E1', lineHeight: 1.6, marginTop: 12 }}>
                A direct line to the team that builds Keystone. No sales funnel, no ticket queue. Email or phone, whichever suits you.
              </p>
              <div style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
                <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 6 }}>Email</div>
                <a href="mailto:hello@cloudvoro.com" style={{ color: '#fff', textDecoration: 'none', fontSize: 17, fontWeight: 600 }}>hello@cloudvoro.com</a>
              </div>
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 6 }}>Phone</div>
                <a href="tel:+353851810310" style={{ color: '#fff', textDecoration: 'none', fontSize: 17, fontWeight: 600 }}>+353 85 181 0310</a>
              </div>
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 6 }}>Book online</div>
                <a href="https://calendly.com/asif-cloudvoro/30min" target="_blank" rel="noopener noreferrer" data-testid="contact-calendly-link" style={{ color: '#fff', textDecoration: 'none', fontSize: 15, fontWeight: 600, wordBreak: 'break-all' }}>calendly.com/asif-cloudvoro/30min</a>
                <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4, fontStyle: 'italic' }}>30-min discovery slot, in your timezone.</div>
              </div>
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 6 }}>Office</div>
                <div style={{ color: '#CBD5E1', fontSize: 14, lineHeight: 1.55 }}>
                  CloudVoro, Ireland<br/>
                  Sister to Intellix IT Solutions, UK
                </div>
              </div>
            </div>

            <div style={{
              background: PALETTE.bgSoft,
              padding: 28,
              borderLeft: `3px solid ${PALETTE.magenta}`,
            }}>
              <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 8 }}>What happens next</div>
              <ol style={{ paddingLeft: 18, margin: '12px 0 0', fontSize: 14, lineHeight: 1.7, color: PALETTE.ink }}>
                <li>We reply within one working day.</li>
                <li>20-min call. We listen, you talk.</li>
                <li>If we fit, written scope within 48h.</li>
                <li>If we don't, a referral to someone better.</li>
              </ol>
            </div>
          </aside>
        </div>
        <style>{`
          @media (max-width: 900px) { .contact-grid { grid-template-columns: 1fr !important; } .form-row { grid-template-columns: 1fr !important; } }
        `}</style>
      </section>

      {/* IMAGE BREAK */}
      <section style={{
        height: 360,
        backgroundImage: `linear-gradient(rgba(10,37,64,0.5), rgba(10,37,64,0.5)), url(${IMG_HERD})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '0 32px',
      }}>
        <div style={{ textAlign: 'center', color: '#fff', maxWidth: 720 }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 20 }}>
            How we work
          </div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(24px, 3.4vw, 36px)', fontWeight: 500, lineHeight: 1.3, letterSpacing: '-0.01em' }}>
            We answer cold emails the same day.
          </div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(20px, 2.8vw, 30px)', fontWeight: 500, fontStyle: 'italic', lineHeight: 1.3, letterSpacing: '-0.01em', color: PALETTE.gold, marginTop: 12 }}>
            Sometimes faster than warm ones.
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
